import { Injectable } from '@angular/core';
import { getDatabase, ref, child, get, set, DatabaseReference } from '@angular/fire/database';
import { StoredAlbum, StoredAudio, StoredPhoto, StoredVideo } from '@shared/types';
import { goOffline, goOnline } from 'firebase/database';


interface PcsetWikiBackup {
  [key: number]: {[innerKey:number]: string}
}

@Injectable({
  providedIn: 'root'
})
export class DatabaseService {
  
  private db = getDatabase();
  private dbRef: DatabaseReference;
  private timeoutId:  ReturnType<typeof setTimeout> | undefined = undefined;
  private inactivityTime = 5000;
  
  constructor() {
    this.dbRef = ref(this.db);
    this.resetInactivityTimer();
  }

  ngOnDestroy() {
    clearTimeout(this.timeoutId);
    this.setOffline();
  }

  private resetInactivityTimer(): void { // after 5 sec sets connection with database offline
    clearTimeout(this.timeoutId);
    goOnline(this.db);
    this.timeoutId = setTimeout(() => {
      this.setOffline();
    }, this.inactivityTime);
  }

  private setOffline(): void {
    goOffline(this.db)
  }

  public async getAudios(): Promise<StoredAudio[]> {
    this.resetInactivityTimer();
    try {
      const snapshot = await get(child(this.dbRef, `/audios`));
      if ( snapshot.exists() ) {
        return snapshot.val();
      }
      else
        return [];
    }
    catch(err) {
      this.handleError('getAudios', err);
    }
  }

  public async setAudios(audios: StoredAudio[]): Promise<boolean> {
    //console.log("SET AUDIOS", os)
    this.resetInactivityTimer();
    try {
      await set(child(this.dbRef, `/audios`), audios);
      return true;
    }
    catch(err) {
      this.handleError('setAuidos', err);
    }
  }


  public async getPhotos(): Promise<StoredPhoto[]> {
    this.resetInactivityTimer();
    try {
      const snapshot = await get(child(this.dbRef, `/photos`));
      if ( snapshot.exists() ) {
        return snapshot.val();
      }
      else
        return [];
    }
    catch(err) {
      this.handleError('getPhotos', err);
    }
  }

  public async setPhotos(photos: StoredPhoto[]): Promise<boolean> {
    //console.log("SET PHOTOS", os)
    this.resetInactivityTimer();
    try {
      await set(child(this.dbRef, `/photos`), photos);
      return true;
    }
    catch(err) {
      this.handleError('setPhotos', err);
    }
  }
  
  public async getVideos(): Promise<StoredVideo[]> {
    this.resetInactivityTimer();
    try {
      const snapshot = await get(child(this.dbRef, `/videos`));
      if ( snapshot.exists() ) {
        return snapshot.val();
      }
      else
        return [];
    }
    catch(err) {
      this.handleError('getVideos', err);
    }
  }

  public async setVideos(videos: StoredVideo[]): Promise<boolean> {
    //console.log("SET VIDEOS", videos)
    this.resetInactivityTimer();
    try {
      await set(child(this.dbRef, `/videos`), videos);
      return true;
    }
    catch(err) {
      this.handleError('setVideos', err);
    }
  }

  public async getAlbums(): Promise<StoredAlbum[]> {
    this.resetInactivityTimer();
    try {
      const snapshot = await get(child(this.dbRef, `/albums`));
      if ( snapshot.exists() ) {
        return snapshot.val();
      }
      else
        return [];
    }
    catch(err) {
      this.handleError('getAlbums', err);
    }
  }

  public async setAlbums(albums: StoredAlbum[]): Promise<boolean> {
    //console.log("SET ALBUMS", os)
    this.resetInactivityTimer();
    try {
      await set(child(this.dbRef, `/albums`), albums);
      return true;
    }
    catch(err) {
      this.handleError('setAlbums', err);
    }
  }

  private handleError(method: string, error: any): never {
    console.error(`ERR ${method} =>`, error);
    throw error;
  }

}